/**
 * MF Overview — platform-wide mutual fund figures, and every investor's MF
 * records in one place.
 *
 * Route: /mf_overview
 * Permission: "MF Overview"
 *
 * The summary is the MF counterpart of the Bonds and IPO tiles on the main
 * dashboard; the records are the lists the investor dashboard shows for one
 * investor, across all of them. APIs: see Api/mfAdminOverview.ts.
 */

import React, { useContext } from "react";
import { appContext } from "../../../App";
import { hasAdminPermission } from "../../../Helpers/adminPermissions";
import MFRecordsSection from "./MFRecordsSection";
import MFSummarySection from "./MFSummarySection";
import { useInvestorDirectory, useSchemeNames } from "./useOverviewLookups";
import type { DirectoryInvestor } from "./useOverviewLookups";
import styles from "./mf-overview.module.css";

const MFOverview: React.FC = () => {
  const { authUser } = useContext(appContext);
  const directory = useInvestorDirectory();
  const schemeNames = useSchemeNames();

  // An investor's name links to their MF dashboard only for admins who may
  // open it; for anyone else it is plain text rather than a dead end.
  const canOpenInvestor = hasAdminPermission(authUser, "MF Investor Profile");
  const openInvestor = (investor: DirectoryInvestor) => {
    const params = new URLSearchParams({
      name: investor.name,
      email: investor.email,
      mobile: investor.mobile,
      smestId: investor.smestId,
    });
    window.open(
      `/mf_investor_profile/${encodeURIComponent(investor.uuid)}/investments?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1>MF Overview</h1>
            <p className={styles.heroCopy}>
              Money moving into and out of mutual funds, and every order and plan behind it,
              across all investors.
            </p>
          </div>
        </section>

        <MFSummarySection />

        <MFRecordsSection
          directory={directory}
          schemeNames={schemeNames}
          onOpenInvestor={canOpenInvestor ? openInvestor : undefined}
        />
      </div>
    </main>
  );
};

export default MFOverview;
